import { address_server_short, API_V2 } from '../config';
import { error } from '../utils/handlerError';
import { axiosInstance } from './common/axios.instance';
import FileSaver from 'file-saver';

const PROJECTS = 'projects';

export async function getAllProjects({ search, sorting } = {}) {
  const { data } = await axiosInstance(`${address_server_short}${API_V2}/${PROJECTS}`, {
    withCredentials: true,
    params: {
      search,
      sorting: sorting?.field && sorting?.type ? JSON.stringify(sorting) : undefined,
    },
  });

  if (data.success) {
    if (data.result) return data.result;
    return [];
  }
  error('getAllProjects', data);
}

export async function getProjectById(id) {
  const { data } = await axiosInstance(`${address_server_short}${API_V2}/${PROJECTS}/${id}`, {
    withCredentials: true,
  });

  if (data.success) {
    if (data.result) return data.result;
    return null;
  }
  error('getProject', data);
}

export async function createProject(project) {
  const { data } = await axiosInstance(`${address_server_short}${API_V2}/${PROJECTS}`, {
    withCredentials: true,
    method: 'POST',
    data: {
      name: project.name,
      code: project.code,
      goal: project.goal,
      task: project.task,
      description: project.description,
    },
  });

  if (!data.success) {
    error('createProject', data);
  }

  return data;
}

export async function updateProject(project) {
  const { data } = await axiosInstance(
    `${address_server_short}${API_V2}/${PROJECTS}/${project.id}`,
    {
      withCredentials: true,
      method: 'PUT',
      data: {
        name: project.name,
        code: project.code,
        goal: project.goal,
        task: project.task,
        description: project.description,
      },
    },
  );

  if (!data.success) {
    error('updateProject', data);
  }

  return data;
}

export async function partialUpdateProject(project) {
  const { data } = await axiosInstance(
    `${address_server_short}${API_V2}/${PROJECTS}/${project.id}`,
    {
      withCredentials: true,
      method: 'PATCH',
      data: {
        id_scheme: project.id_scheme,
        devices: project.devices,
      },
    },
  );

  if (!data.success) {
    error('partialUpdateProject', data);
  }

  return data;
}

export async function deleteProject(id) {
  const { data } = await axiosInstance(`${address_server_short}${API_V2}/${PROJECTS}/${id}`, {
    withCredentials: true,
    method: 'DELETE',
  });

  if (!data.success) {
    error('deleteProject', data);
  }

  return data;
}

export async function getAllIterations({ search, sorting }) {
  const { data } = await axiosInstance(`${address_server_short}${API_V2}/${PROJECTS}/iterations`, {
    withCredentials: true,
    params: {
      search,
      sorting: sorting?.field && sorting?.type ? JSON.stringify(sorting) : undefined,
    },
  });

  if (!data.success) {
    error('getIterations', data);
  }

  return data;
}

export async function createProjectIteration(id) {
  const { data } = await axiosInstance(
    `${address_server_short}${API_V2}/${PROJECTS}/${id}/iterations`,
    {
      withCredentials: true,
      method: 'POST',
    },
  );

  if (!data.success) {
    error('createIteration', data);
  }

  return data;
}

export async function startProcessIteration(id, data) {
  const response = await axiosInstance(
    `${address_server_short}${API_V2}/${PROJECTS}/iterations/process/${id}/start`,
    {
      withCredentials: true,
      method: 'POST',
      data,
    },
  );

  if (!response?.data.success) {
    error('createIteration', response?.data);
  }

  return response?.data;
}

export async function stopProcessIteration(id, data) {
  const formData = new FormData();

  if (data.params) formData.append('params', JSON.stringify(data.params));
  if (data.objects) formData.append('objects', JSON.stringify(data.objects));
  if (data.files && Array.isArray(data.files)) {
    data.files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const response = await axiosInstance(
    `${address_server_short}${API_V2}/${PROJECTS}/iterations/process/${id}/stop`,
    {
      withCredentials: true,
      method: 'POST',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );

  if (!response.data.success) {
    error('stopProcessIteration', response.data);
  }

  return response.data;
}

export async function deleteProjectIteration(id) {
  const { data } = await axiosInstance(
    `${address_server_short}${API_V2}/${PROJECTS}/iterations/${id}`,
    {
      withCredentials: true,
      method: 'DELETE',
    },
  );

  if (!data.success) {
    error('deleteIteration', data);
  }

  return data;
}

export async function forceCompleteIteration(id_iteration, data) {
  const response = await axiosInstance(
    `${address_server_short}${API_V2}/${PROJECTS}/iterations/${id_iteration}/force-complete`,
    {
      withCredentials: true,
      method: 'POST',
      data,
    },
  );

  if (!response.data.success) {
    error('forceCompleteIteration', response.data);
  }

  return response.data;
}

export async function getProjectIteration(id) {
  const { data } = await axiosInstance(
    `${address_server_short}${API_V2}/${PROJECTS}/iterations/${id}`,
    {
      withCredentials: true,
      method: 'GET',
    },
  );

  if (!data.success) {
    error('getIteration', data);
  }

  return data;
}

export async function createProjectDocument(id, file) {
  const formData = new FormData();
  formData.append('files', file);

  const { data } = await axiosInstance(`${address_server_short}${API_V2}/${PROJECTS}/${id}/files`, {
    withCredentials: true,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    method: 'POST',
    data: formData,
  });

  if (!data.success) {
    error('createDocument', data);
  }

  return data;
}

export async function deleteProjectDocunent(id) {
  const { data } = await axiosInstance(`${address_server_short}${API_V2}/${PROJECTS}/files/${id}`, {
    withCredentials: true,
    method: 'DELETE',
  });

  if (!data.success) {
    error('deleteDocument', data);
  }

  return data;
}

export async function startIterationProduction(id) {
  const { data } = await axiosInstance(
    `${address_server_short}${API_V2}/${PROJECTS}/iterations/${id}/init`,
    {
      withCredentials: true,
      method: 'POST',
    },
  );

  if (!data.success) {
    error('startIterationProduction', data);
  }

  return data;
}

export async function addPerformerToProject(projectId, userId) {
  const { data } = await axiosInstance(
    `${address_server_short}${API_V2}/${PROJECTS}/${projectId}/users`,
    {
      withCredentials: true,
      method: 'POST',
      data: { id: userId },
    },
  );

  if (!data.success) {
    error('addPerformerToProject', data);
  }

  return data;
}

export async function deletePerformerToProject(projectId, userId) {
  const { data } = await axiosInstance(
    `${address_server_short}${API_V2}/${PROJECTS}/${projectId}/users/${userId}`,
    {
      withCredentials: true,
      method: 'DELETE',
    },
  );

  if (!data.success) {
    error('deletePerformerToProject', data);
  }

  return data;
}

export async function downloadIterationReport(id) {
  const response = await axiosInstance(
    `${address_server_short}${API_V2}/${PROJECTS}/iterations/${id}/report`,
    {
      withCredentials: true,
      method: 'GET',
      responseType: 'blob',
    },
  );

  const contentDisposition = response.headers['content-disposition'];
  const fileType = response.headers['content-type'];

  let fileName = 'report.pdf';
  if (contentDisposition) {
    const match = contentDisposition.match(/filename\*\=UTF-8''([^;]+)/);
    if (match && match[1]) {
      fileName = decodeURIComponent(match[1]);
    }
  }

  const blob = new Blob([response.data], { type: fileType });
  FileSaver.saveAs(blob, fileName);
}
